import { useState, type ReactNode } from "react";
import {
  ChevronIcon,
  DatabaseIcon,
  SearchIcon,
  ExtensionsIcon,
  HatcheryIcon,
  ServerIcon,
  SyncIcon,
  Text,
  Tooltip,
  WorkflowIcon,
} from "@carbon6/c6-frontend-design";
import { appTextModes } from "../theme";
import { GnbButton } from "./GnbButton";
import styles from "./Gnb.module.css";

export enum GnbMenu {
  ScannedResults = "scannedResults",
  // 외부 데이터셋(HF·Azure 코퍼스)의 등록·revision·hide/show. 상세의 탭이 Probe·샘플 점검(Sim)·
  // InputParams 편집을 든다(MLP-712 S1·S2·S3).
  Datasets = "datasets",
  // 조직 전체의 변환·등록 실행 목록. 실패 처리·force·등록 재시도가 여기서 끝난다(S7·S9).
  Conversions = "conversions",
  // 이미지 릴리스와 함께 BE 에 배포된 템플릿 카탈로그 — 읽기 전용(S3·S8).
  Templates = "templates",
  // 수집 Worker 현황. 운영 변경은 CLI 전용이라 FE 는 관측만 한다(명세 §12).
  OperationsWorkers = "operationsWorkers",
  // Airflow dispatcher/reconciler·outbox 동기화 현황 — 관측만(S4).
  OperationsAirflow = "operationsAirflow",
}

export type GnbMenuEntry = {
  label: string;
  to: string;
  icon: ReactNode;
};

// 메뉴 → 라벨·경로·아이콘. 라우터도 이 표의 경로를 쓴다 — 항목과 라우트가 따로 갈리지 않는다.
export const gnbMenuEntries: Record<GnbMenu, GnbMenuEntry> = {
  [GnbMenu.ScannedResults]: {
    label: "Scanned Results",
    to: "/scanned-results",
    icon: <SearchIcon />,
  },
  [GnbMenu.Datasets]: { label: "Sampled Dataset", to: "/datasets", icon: <DatabaseIcon /> },
  [GnbMenu.Conversions]: { label: "Conversions", to: "/conversions", icon: <WorkflowIcon /> },
  [GnbMenu.Templates]: { label: "Templates", to: "/templates", icon: <ExtensionsIcon /> },
  [GnbMenu.OperationsWorkers]: {
    label: "Workers",
    to: "/operations/workers",
    icon: <ServerIcon />,
  },
  [GnbMenu.OperationsAirflow]: {
    label: "Airflow",
    to: "/operations/airflow",
    icon: <SyncIcon />,
  },
};

type GnbSection = {
  // 접힘 상태 저장·React key 용 식별자.
  key: string;
  // 섹션 헤더 라벨. 없으면 primary(헤더 없이 항상 노출되는 상단 그룹).
  title?: string;
  items: readonly GnbMenu[];
};

// Intake 는 Org/Member 만 있고 role 사다리가 없다 — 섹션·항목에 권한 게이트를 두지 않는다.
const gnbSections: readonly GnbSection[] = [
  {
    // Workspace — 원본 → Probe/POC → 변환·등록 흐름의 조직 전체 목록 세 개. 항상 펼쳐 둔다.
    key: "workspace",
    items: [GnbMenu.ScannedResults, GnbMenu.Datasets, GnbMenu.Conversions, GnbMenu.Templates],
  },
  {
    // Operations — CLI 전용 운영의 관측 축. 업무 흐름과 구분해 접이식 섹션으로 둔다.
    key: "operations",
    title: "Operations",
    items: [GnbMenu.OperationsWorkers, GnbMenu.OperationsAirflow],
  },
];

const collapsedSectionsStorageKey = "spawning-pool:gnb-collapsed-sections";

type GnbProps = {
  currentMenu: GnbMenu;
  appName?: string;
  collapsed?: boolean;
  // 브랜드 타이틀 옆 슬롯 — 계정 메뉴 chevron 등. 데이터·핸들러 소유는 호출자(PageBase)에 둔다.
  brandMenu?: ReactNode;
  // 브랜드 행 우측에 둘 collapse 토글 등. 버튼 소유는 호출자(PageBase)에 둔다.
  collapseControl?: ReactNode;
  // 배포 정보와 업데이트 안내는 브랜드 아래 독립 행을 쓴다.
  serverContext?: ReactNode;
  banner?: ReactNode;
};

export function Gnb({
  appName = "SPNP",
  brandMenu,
  collapseControl,
  collapsed = false,
  currentMenu,
  serverContext,
  banner,
}: GnbProps) {
  // 접힌 섹션 key 집합 — localStorage 로 상태를 기억한다.
  const [collapsedSections, setCollapsedSections] = useState<ReadonlySet<string>>(() =>
    readCollapsedSections(),
  );

  function toggleSection(key: string) {
    setCollapsedSections((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      writeCollapsedSections(next);
      return next;
    });
  }

  return (
    <div className={styles.shell} data-collapsed={collapsed ? "true" : "false"}>
      <div className={styles.brand}>
        {/* 접힘 레일에선 마크를 맨 아래 계정 트리거로 옮기므로 브랜드 행에선 숨긴다. */}
        {collapsed ? null : (
          <>
            <HatcheryIcon className={styles.brandLogo} />
            <Text variant={appTextModes["title-bold"]} className={styles.brandTitle}>
              {appName}
            </Text>
            {brandMenu ? <div className={styles.brandMenu}>{brandMenu}</div> : null}
          </>
        )}
        {collapseControl ? <div className={styles.brandAccessory}>{collapseControl}</div> : null}
      </div>

      {serverContext ? <div className={styles.serverContext}>{serverContext}</div> : null}
      {banner}

      <nav className={styles.nav} aria-label="전역 메뉴">
        {gnbSections.map((section) => {
          // primary(헤더 없음) 또는 접힘 레일: 헤더 없이 항목만 아이콘으로 나열.
          if (section.title == null || collapsed) {
            return (
              <div key={section.key} className={styles.section}>
                {section.items.map((menu) => (
                  <NavItem key={menu} menu={menu} currentMenu={currentMenu} collapsed={collapsed} />
                ))}
              </div>
            );
          }

          const sectionCollapsed = collapsedSections.has(section.key);
          return (
            <div key={section.key} className={styles.section}>
              <button
                type="button"
                className={styles.sectionHeader}
                aria-expanded={!sectionCollapsed}
                onClick={() => toggleSection(section.key)}
              >
                <span className={styles.sectionTitle}>{section.title}</span>
                <ChevronIcon
                  className={styles.sectionChevron}
                  data-collapsed={sectionCollapsed ? "true" : "false"}
                />
              </button>
              {sectionCollapsed
                ? null
                : section.items.map((menu) => (
                    <NavItem
                      key={menu}
                      menu={menu}
                      currentMenu={currentMenu}
                      collapsed={collapsed}
                    />
                  ))}
            </div>
          );
        })}
      </nav>
    </div>
  );
}

function NavItem({
  menu,
  currentMenu,
  collapsed,
}: {
  menu: GnbMenu;
  currentMenu: GnbMenu;
  collapsed: boolean;
}) {
  const entry = gnbMenuEntries[menu];
  return (
    // 접힘 레일에서만 라벨을 툴팁으로 낸다 — falsy content 는 c6 Tooltip 이 래퍼만 남기고 비활성이다.
    <Tooltip content={collapsed ? entry.label : null} placement="right">
      <GnbButton
        className={styles.navItem}
        to={entry.to}
        current={menu === currentMenu}
        aria-label={collapsed ? entry.label : undefined}
      >
        <span className={styles.navIcon} aria-hidden="true">
          {entry.icon}
        </span>
        <span className={styles.fullLabel}>{entry.label}</span>
      </GnbButton>
    </Tooltip>
  );
}

function readCollapsedSections(): ReadonlySet<string> {
  if (typeof window === "undefined") {
    return new Set();
  }
  try {
    const raw = window.localStorage.getItem(collapsedSectionsStorageKey);
    if (!raw) {
      return new Set();
    }
    const parsed: unknown = JSON.parse(raw);
    return Array.isArray(parsed)
      ? new Set(parsed.filter((key): key is string => typeof key === "string"))
      : new Set();
  } catch {
    return new Set();
  }
}

function writeCollapsedSections(keys: ReadonlySet<string>) {
  if (typeof window === "undefined") {
    return;
  }
  try {
    window.localStorage.setItem(collapsedSectionsStorageKey, JSON.stringify([...keys]));
  } catch {
    // Storage can be unavailable in private or restricted browser contexts.
  }
}
